import {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { CycleTab, cycleNavigationFor, type CycleSection } from "./CycleTab";
import {
  api,
  type CampaignSetup,
  type MediaAsset,
} from "./api";
import { PreparationView, proposalDraftChecks } from "./campaign-setup/PreparationView";
import { CouncilTab } from "./campaign-setup/CouncilTab";
import { DossierTab } from "./campaign-setup/DossierTab";
import { ChronicleTab } from "./campaign-setup/ChronicleTab";
import { WorldTab } from "./campaign-setup/WorldTab";
import { OwnerBetaToolsModal } from "./campaign-setup/OwnerBetaTools";
import {
  ProgressBar,
  Spinner,
  GAME_UI_ASSET_BASE,
  type ChronicleSection,
  type WorldSection,
  type CouncilSection,
  type DossierSection,
  type IconName,
} from "./campaign-setup/shared";

interface Props {
  campaignId: string;
  onBack: () => void;
}
type TabId = "cycle" | "chronicle" | "world" | "council" | "dossier";
type SecondaryNavigationItem = {
  id: string;
  label: string;
  badge?: string;
  unread?: boolean;
};

const KNOWN_CYCLE_SECTIONS: CycleSection[] = [
  "situation",
  "actions",
  "chronicle",
  "hq",
];

function isKnownCycleSection(value: string): value is CycleSection {
  return KNOWN_CYCLE_SECTIONS.includes(value as CycleSection);
}

export function CampaignSetupPage({
  campaignId,
  onBack,
}: Props) {
  const [setup, setSetup] = useState<CampaignSetup | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncProblem, setSyncProblem] = useState(false);
  const [recentlyUpdated, setRecentlyUpdated] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [worldName, setWorldName] = useState("");
  const [concept, setConcept] = useState("");
  const [activeTab, setActiveTab] = useState<TabId>("cycle");
  const [cycleSection, setCycleSection] = useState<CycleSection>("situation");
  const [chronicleSection, setChronicleSection] =
    useState<ChronicleSection>("chapters");
  const [worldSection, setWorldSection] = useState<WorldSection>("overview");
  const [councilSection, setCouncilSection] =
    useState<CouncilSection>("members");
  const [ownerToolsOpen, setOwnerToolsOpen] = useState(false);
  const [dossierSection, setDossierSection] = useState<DossierSection>("intro");
  const proposalDraftInitialized = useRef(false);
  const setupRef = useRef<CampaignSetup | null>(null);
  const loadRequestRef = useRef<Promise<void> | null>(null);
  const mountedRef = useRef(true);
  const busyRef = useRef(false);
  const updateIndicatorTimerRef = useRef<number | null>(null);

  const load = useCallback(
    async (silent = false, force = false) => {
      if (loadRequestRef.current && !force) return loadRequestRef.current;

      let refreshingTimer: number | undefined;
      const request = (async () => {
        if (silent)
          refreshingTimer = window.setTimeout(() => setRefreshing(true), 450);
        else if (!setupRef.current) setLoading(true);

        try {
          const result = await api.campaignSetup(campaignId);
          if (!mountedRef.current) return;

          const previous = setupRef.current;
          const changed = Boolean(
            previous && setupDigest(previous) !== setupDigest(result),
          );
          setupRef.current = result;
          setSetup(result);

          if (!proposalDraftInitialized.current) {
            const ownProposal = result.worldSelection.proposals.find(
              (proposal) => proposal.isMine,
            );
            setWorldName(ownProposal?.worldName ?? result.campaign.title);
            setConcept(ownProposal?.concept ?? "");
            proposalDraftInitialized.current = true;
          }

          if (changed) {
            setRecentlyUpdated(true);
            if (updateIndicatorTimerRef.current)
              window.clearTimeout(updateIndicatorTimerRef.current);
            updateIndicatorTimerRef.current = window.setTimeout(
              () => setRecentlyUpdated(false),
              2_500,
            );
          }

          setSyncProblem(false);
          setError(null);
        } catch (cause) {
          if (!mountedRef.current) return;
          if (silent) setSyncProblem(true);
          else
            setError(
              cause instanceof Error
                ? cause.message
                : "Не удалось загрузить кампанию",
            );
        } finally {
          if (refreshingTimer) window.clearTimeout(refreshingTimer);
          if (mountedRef.current) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      })();

      loadRequestRef.current = request;
      try {
        await request;
      } finally {
        if (loadRequestRef.current === request) loadRequestRef.current = null;
      }
    },
    [campaignId],
  );

  useEffect(() => {
    mountedRef.current = true;
    proposalDraftInitialized.current = false;
    setupRef.current = null;
    setSetup(null);
    setLoading(true);
    setSyncProblem(false);
    void load();

    return () => {
      mountedRef.current = false;
      if (updateIndicatorTimerRef.current)
        window.clearTimeout(updateIndicatorTimerRef.current);
    };
  }, [campaignId, load]);

  const needsFastRefresh = Boolean(
    setup &&
    ((!setup.world &&
      [
        "WORLD_GENERATING",
        "RANDOM_ROLE_ASSIGNMENT",
        "PRIVATE_BRIEFING",
      ].includes(setup.worldSelection.phase)) ||
      setup.world?.openingImages.some((asset) =>
        ["PENDING", "GENERATING", "RETRY"].includes(asset.status),
      ) ||
      (setup.cycle.current &&
        ["PREPARING", "AGENDA", "RESOLVING", "NARRATING"].includes(
          setup.cycle.current.phase,
        )) ||
      setup.cycle.current?.openingImages.some((asset) =>
        ["PENDING", "GENERATING", "RETRY"].includes(asset.status),
      ) ||
      setup.cycle.current?.closingImages.some((asset) =>
        ["PENDING", "GENERATING", "RETRY"].includes(asset.status),
      ) ||
      setup.cycle.current?.actions.some(
        (cycleAction) =>
          cycleAction.narrativeStatus !== "READY" ||
          cycleAction.images.some((asset) =>
            ["PENDING", "GENERATING", "RETRY"].includes(asset.status),
          ),
      )),
  );
  const refreshIntervalMs = needsFastRefresh ? 2_500 : 5_000;

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible" && !busyRef.current)
        void load(true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshWhenVisible();
    };

    const timer = window.setInterval(refreshWhenVisible, refreshIntervalMs);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("online", refreshWhenVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("online", refreshWhenVisible);
    };
  }, [load, refreshIntervalMs]);

  async function action(
    label: string,
    run: () => Promise<unknown>,
  ): Promise<boolean> {
    busyRef.current = true;
    setBusyLabel(label);
    setError(null);
    try {
      await run();
      await load(true, true);
      return true;
    } catch (cause) {
      const message =
        cause instanceof Error ? cause.message : "Операция не выполнена";
      // Long-running operations may change the server phase before failing. Reload first,
      // then keep the operation error visible so a retry action appears immediately.
      await load(true);
      setError(message);
      return false;
    } finally {
      busyRef.current = false;
      setBusyLabel(null);
    }
  }

  async function submit(event: FormEvent): Promise<boolean> {
    event.preventDefault();
    if (
      !worldName.trim() ||
      !proposalDraftChecks(worldName, concept).every((check) => check.done)
    )
      return false;
    return action("Проверяем и сохраняем предложение…", () =>
      api.submitWorldProposal(campaignId, worldName, concept),
    );
  }

  if (loading) return <FullPageLoader label="Открываем хронику кампании…" />;
  if (!setup)
    return (
      <main className="shell">
        <button className="back-button" onClick={onBack}>
          ← К кампаниям
        </button>
        <section className="error-card">
          {error ?? "Кампания недоступна"}
          <button onClick={() => void load()}>Повторить</button>
        </section>
      </main>
    );

  const currentMember = setup.members.find((member) => member.isCurrentUser);
  const readyForPlay = Boolean(
    setup.world && setup.rolesAssigned && setup.setupStage === "READY",
  );
  const availableTabs: Array<{
    id: TabId;
    label: string;
    icon: IconName;
    badge?: string;
    unread?: boolean;
  }> = setup.world
    ? [
        {
          id: "cycle",
          label: "Штаб",
          icon: "cycle",
          badge: cycleTabBadge(setup),
          unread: false,
        },
        ...(setup.myPrivateProfile
          ? [
              {
                id: "dossier" as TabId,
                label: "Досье",
                icon: "lock" as IconName,
              },
            ]
          : []),
        {
          id: "chronicle",
          label: "Летопись",
          icon: "book",
          badge: imageTabBadge(setup.world.openingImages),
        },
        { id: "world", label: "Мир", icon: "world" },
        { id: "council", label: "Совет", icon: "users" },
      ]
    : [];
  const selectedTab = availableTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : (availableTabs[0]?.id ?? activeTab);
  const cycleNavigation = cycleNavigationFor(setup.cycle.current);
  const selectedCycleSection =
    isKnownCycleSection(cycleSection) &&
    cycleNavigation.some((item) => item.id === cycleSection)
      ? cycleSection
      : cycleNavigation[0].id;
  const chronicleNavigation: Array<{
    id: ChronicleSection;
    label: string;
    badge?: string;
  }> = [
    ...(setup.cycle.chronicleChapters.length > 0
      ? [
          {
            id: "chapters" as ChronicleSection,
            label: "Главы кампании",
            badge: `${setup.cycle.chronicleChapters.length}`,
          },
        ]
      : []),
    ...(setup.continuity.history.length > 0 ||
    setup.continuity.archived.length > 0
      ? [
          {
            id: "threads" as ChronicleSection,
            label: "Линии и последствия",
            badge: `${setup.continuity.history.length}`,
          },
        ]
      : []),
    ...(setup.threats.recentEvents.length > 0 || setup.threats.defeat
      ? [
          {
            id: "threats" as ChronicleSection,
            label: "Угрозы и поражение",
            badge: setup.threats.defeat
              ? "!"
              : `${setup.threats.recentEvents.length}`,
          },
        ]
      : []),
    ...(setup.cycle.rosterEvents.length > 0
      ? [
          {
            id: "council" as ChronicleSection,
            label: "Состав Совета",
            badge: `${setup.cycle.rosterEvents.length}`,
          },
        ]
      : []),
    { id: "prologue", label: "Пролог мира" },
  ];
  const relationshipAlertCount =
    setup.relationships.factions.filter(
      (item) => item.posture === "HOSTILE" || item.posture === "WARY",
    ).length +
    setup.relationships.institutions.filter(
      (item) => item.strain === "OVERLOADED" || item.strain === "TENSE",
    ).length;
  const betaBadge =
    setup.betaReadiness.blockers > 0
      ? "!"
      : setup.betaReadiness.warnings > 0
        ? `${setup.betaReadiness.warnings}`
        : undefined;
  const secondaryItems: SecondaryNavigationItem[] =
    selectedTab === "cycle"
      ? cycleNavigation
      : selectedTab === "chronicle"
        ? chronicleNavigation
        : selectedTab === "world"
          ? [
              { id: "overview", label: "Обзор" },
              { id: "institutions", label: "Институты" },
              { id: "factions", label: "Фракции" },
            ]
          : selectedTab === "council"
            ? [
                { id: "members", label: "Состав" },
                {
                  id: "relationships",
                  label: "Политическая среда",
                  badge:
                    relationshipAlertCount > 0
                      ? `${relationshipAlertCount}`
                      : undefined,
                },
              ]
            : [
                { id: "intro", label: "Персонаж" },
                { id: "goal", label: "Цели" },
                { id: "briefing", label: "Сведения" },
              ];
  const selectedChronicleSection: ChronicleSection = chronicleNavigation.some(
    (item) => item.id === chronicleSection,
  )
    ? chronicleSection
    : chronicleNavigation[0].id;
  const rawSelectedSecondary =
    selectedTab === "cycle"
      ? selectedCycleSection
      : selectedTab === "chronicle"
        ? selectedChronicleSection
        : selectedTab === "world"
          ? worldSection
          : selectedTab === "council"
            ? councilSection
            : selectedTab === "dossier"
              ? dossierSection
              : "prologue";
  const selectedSecondary = secondaryItems.some((item) => item.id === rawSelectedSecondary)
    ? rawSelectedSecondary
    : (secondaryItems[0]?.id ?? rawSelectedSecondary);

  function selectSecondary(id: string) {
    if (selectedTab === "cycle") setCycleSection(id as CycleSection);
    else if (selectedTab === "chronicle")
      setChronicleSection(id as ChronicleSection);
    else if (selectedTab === "world") setWorldSection(id as WorldSection);
    else if (selectedTab === "council") setCouncilSection(id as CouncilSection);
    else if (selectedTab === "dossier") setDossierSection(id as DossierSection);
  }

  return (
    <main
      className={`shell setup-shell cc-campaign-page${readyForPlay ? " cc-structured-page cc-game-screen" : ""}`}
    >
      {busyLabel && <ActivityOverlay label={busyLabel} />}

      {readyForPlay && setup.world ? (
        <>
          <div className="cc-game-interface">
            <CampaignGameTopbar
              setup={setup}
              onBack={onBack}
              refreshing={refreshing}
              syncProblem={syncProblem}
              recentlyUpdated={recentlyUpdated}
              canManage={setup.canManage}
              betaBadge={betaBadge}
              onOpenOwnerTools={() => setOwnerToolsOpen(true)}
            />

            {selectedTab !== "cycle" && (
              <div className="cc-room-subnav">
                <SecondaryNavigation
                  items={secondaryItems}
                  selectedId={selectedSecondary}
                  onSelect={selectSecondary}
                />
              </div>
            )}
          </div>

          {error && (
            <section className="error-card cc-error cc-structured-error">
              {error}
              <button type="button" onClick={() => setError(null)}>
                Закрыть
              </button>
            </section>
          )}

          <section className="cc-structured-content">
            {selectedTab === "cycle" && (
              <CycleTab
                campaignId={campaignId}
                setup={setup}
                action={action}
                activeSection={selectedCycleSection}
                onSectionChange={setCycleSection}
                onOpenTab={setActiveTab}
              />
            )}
            {selectedTab === "chronicle" && (
              <ChronicleTab
                campaignId={campaignId}
                world={setup.world}
                continuity={setup.continuity}
                threats={setup.threats}
                chapters={setup.cycle.chronicleChapters}
                rosterEvents={setup.cycle.rosterEvents}
                section={selectedChronicleSection}
                canManage={setup.canManage}
                action={action}
              />
            )}
            {selectedTab === "world" && (
              <WorldTab
                world={setup.world}
                continuity={setup.continuity}
                threats={setup.threats}
                infrastructure={setup.infrastructure}
                section={selectedSecondary as WorldSection}
              />
            )}
            {selectedTab === "council" && (
              <CouncilTab
                campaignId={campaignId}
                setup={setup}
                currentMember={currentMember}
                action={action}
                section={councilSection}
              />
            )}
            {selectedTab === "dossier" && (
              <DossierTab
                campaignId={campaignId}
                setup={setup}
                section={dossierSection}
                action={action}
              />
            )}
          </section>

          {readyForPlay && (
            <CampaignRoomNavigation
              setup={setup}
              selectedTab={selectedTab}
              selectedCycleSection={selectedCycleSection}
              onOpenWorld={() => {
                setActiveTab("cycle");
                setCycleSection("situation");
              }}
              onOpenActions={() => {
                setActiveTab("cycle");
                setCycleSection("actions");
              }}
              onOpenChronicle={() => {
                setActiveTab("cycle");
                setCycleSection("chronicle");
              }}
              onOpenHub={() => {
                setActiveTab("cycle");
                setCycleSection("hq");
              }}
            />
          )}
        </>
      ) : (
        <>
          <div className="cc-game-interface cc-setup-interface">
            <CampaignPreparationTopbar
              setup={setup}
              onBack={onBack}
              refreshing={refreshing}
              syncProblem={syncProblem}
              recentlyUpdated={recentlyUpdated}
            />
          </div>

          {error && (
            <section className="error-card cc-error">
              {error}
              <button type="button" onClick={() => setError(null)}>
                Закрыть
              </button>
            </section>
          )}

          <PreparationView
            setup={setup}
            worldName={worldName}
            concept={concept}
            setWorldName={setWorldName}
            setConcept={setConcept}
            submit={submit}
            campaignId={campaignId}
            action={action}
          />
        </>
      )}

      {readyForPlay && ownerToolsOpen && setup.canManage && (
        <OwnerBetaToolsModal
          campaignId={campaignId}
          setup={setup}
          action={action}
          onClose={() => setOwnerToolsOpen(false)}
        />
      )}

      <footer>City Chronicle MVP 9.0.2 · setup layout polish</footer>
    </main>
  );
}

function CampaignPreparationTopbar({
  setup,
  onBack,
  refreshing,
  syncProblem,
  recentlyUpdated,
}: {
  setup: CampaignSetup;
  onBack: () => void;
  refreshing: boolean;
  syncProblem: boolean;
  recentlyUpdated: boolean;
}) {
  const status = refreshing
    ? "Синхронизация"
    : syncProblem
      ? "Нет связи"
      : recentlyUpdated
        ? "Обновлено"
        : "Онлайн";
  const statusClass = syncProblem
    ? " warning"
    : recentlyUpdated
      ? " changed"
      : "";
  return (
    <header className="cc-game-topbar cc-setup-topbar" aria-label="Подготовка кампании">
      <button
        className="cc-game-back"
        type="button"
        onClick={onBack}
        aria-label="Вернуться к кампаниям"
      >
        ‹
      </button>
      <div className="cc-game-title">
        <span>{setup.campaign.title}</span>
        <small>{stageLabel(setup.setupStage)}</small>
      </div>
      <span className={`cc-game-sync${statusClass}`} aria-live="polite">
        {refreshing ? <Spinner small /> : <i />}
        {status}
      </span>
    </header>
  );
}

function CampaignGameTopbar({
  setup,
  onBack,
  refreshing,
  syncProblem,
  recentlyUpdated,
  canManage,
  betaBadge,
  onOpenOwnerTools,
}: {
  setup: CampaignSetup;
  onBack: () => void;
  refreshing: boolean;
  syncProblem: boolean;
  recentlyUpdated: boolean;
  canManage: boolean;
  betaBadge?: string;
  onOpenOwnerTools: () => void;
}) {
  const cycle = setup.cycle.current;
  const status = refreshing
    ? "Синхронизация"
    : syncProblem
      ? "Нет связи"
      : recentlyUpdated
        ? "Обновлено"
        : "Онлайн";
  const statusClass = syncProblem
    ? " warning"
    : recentlyUpdated
      ? " changed"
      : "";
  return (
    <header className="cc-game-topbar" aria-label="Кампания">
      <button
        className="cc-game-back"
        type="button"
        onClick={onBack}
        aria-label="Вернуться к кампаниям"
      >
        ‹
      </button>
      <div className="cc-game-title">
        <span>{setup.world?.worldName ?? setup.campaign.title}</span>
        <small>
          {cycle
            ? `Цикл ${cycle.cycleNumber} · ${phaseShortLabel(cycle.phase)}`
            : "Штаб кампании"}
        </small>
      </div>
      <div className="cc-game-topbar-actions">
        {canManage && (
          <button
            className="cc-game-owner-tools"
            type="button"
            onClick={onOpenOwnerTools}
            aria-label="Открыть тестирование беты"
            title="Тестирование беты"
          >
            <span aria-hidden="true">⚙</span>
            {betaBadge && <b>{betaBadge}</b>}
          </button>
        )}
        <span className={`cc-game-sync${statusClass}`} aria-live="polite">
          {refreshing ? <Spinner small /> : <i />}
          {status}
        </span>
      </div>
    </header>
  );
}

function CampaignRoomNavigation({
  setup,
  selectedTab,
  selectedCycleSection,
  onOpenWorld,
  onOpenActions,
  onOpenChronicle,
  onOpenHub,
}: {
  setup: CampaignSetup;
  selectedTab: TabId;
  selectedCycleSection: CycleSection;
  onOpenWorld: () => void;
  onOpenActions: () => void;
  onOpenChronicle: () => void;
  onOpenHub: () => void;
}) {
  const cycle = setup.cycle.current;
  const isCycle = selectedTab === "cycle";
  const actionBadge = cycle?.phase === "DAY"
    ? `${cycle.actionsUsed}/${cycle.actionLimit}`
    : cycle?.phase === "AGENDA" || cycle?.phase === "VOTING"
      ? `${cycle.initiatives.filter((item) => item.status !== "MERGED" && item.consolidatedIntoId == null).length}`
      : undefined;
  const hqBadge = cycle ? cycle.unread.chatCount + cycle.unread.initiativeCount : 0;
  const worldBadge = cycle?.worldChanges.length || cycle?.unread.chronicleCount || undefined;
  return (
    <nav className="cc-game-room-nav cc-game-room-nav-900" aria-label="Основная навигация">
      <CampaignRoomNavButton
        icon="icon_room_world.png"
        label="Мир"
        active={isCycle && selectedCycleSection === "situation"}
        badge={worldBadge ? String(worldBadge) : undefined}
        onClick={onOpenWorld}
      />
      <CampaignRoomNavButton
        icon="icon_room_actions.png"
        label="Действия"
        active={isCycle && selectedCycleSection === "actions"}
        badge={cycle?.canAct ? "!" : actionBadge}
        onClick={onOpenActions}
      />
      <CampaignRoomNavButton
        icon="icon_room_chronicle.png"
        label="Хроника"
        active={isCycle && selectedCycleSection === "chronicle"}
        badge={cycle?.unread.chronicleCount ? String(cycle.unread.chronicleCount) : undefined}
        onClick={onOpenChronicle}
      />
      <CampaignRoomNavButton
        icon="icon_dossier.png"
        label="Штаб"
        active={isCycle && selectedCycleSection === "hq"}
        badge={hqBadge ? String(hqBadge) : undefined}
        onClick={onOpenHub}
      />
    </nav>
  );
}

function CampaignRoomNavButton({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: string;
  label: string;
  active: boolean;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? "active" : ""}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
    >
      <img src={`${GAME_UI_ASSET_BASE}/${icon}`} alt="" />
      <span>{label}</span>
      {badge && <b>{badge}</b>}
    </button>
  );
}

function phaseShortLabel(
  phase: NonNullable<CampaignSetup["cycle"]["current"]>["phase"],
) {
  return (
    {
      PREPARING: "подготовка",
      DAY: "день действий",
      DAY_CLOSING: "последние встречи",
      AGENDA: "повестка",
      VOTING: "голосование",
      RESOLVING: "расчёт",
      NARRATING: "хроника",
      PUBLISHED: "завершён",
    } as const
  )[phase];
}

function ActivityOverlay({ label }: { label: string }) {
  return (
    <div className="cc-activity-overlay">
      <div>
        <Spinner />
        <h2>{label}</h2>
        <ProgressBar indeterminate />
        <p>
          Действие может занять некоторое время. Состояние кампании сохранится
          на сервере.
        </p>
      </div>
    </div>
  );
}
function FullPageLoader({ label }: { label: string }) {
  return (
    <main className="cc-full-loader">
      <div>
        <Spinner />
        <h2>{label}</h2>
        <ProgressBar indeterminate />
      </div>
    </main>
  );
}
function SecondaryNavigation({
  items,
  selectedId,
  onSelect,
}: {
  items: SecondaryNavigationItem[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [edge, setEdge] = useState({
    left: false,
    right: false,
    scrollable: false,
  });

  const refreshEdge = useCallback(() => {
    const element = scrollerRef.current;
    if (!element) return;
    const maxScroll = Math.max(0, element.scrollWidth - element.clientWidth);
    const next = {
      left: element.scrollLeft > 2,
      right: element.scrollLeft < maxScroll - 2,
      scrollable: maxScroll > 2,
    };
    setEdge((current) =>
      current.left === next.left &&
      current.right === next.right &&
      current.scrollable === next.scrollable
        ? current
        : next,
    );
  }, []);

  useEffect(() => {
    const element = scrollerRef.current;
    if (!element) return;

    const onScroll = () => refreshEdge();
    element.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", refreshEdge);

    let resizeObserver: ResizeObserver | null = null;
    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(refreshEdge);
      resizeObserver.observe(element);
      Array.from(element.children).forEach((child) =>
        resizeObserver?.observe(child),
      );
    }

    const timer = window.setTimeout(refreshEdge, 0);
    refreshEdge();

    return () => {
      element.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", refreshEdge);
      resizeObserver?.disconnect();
      window.clearTimeout(timer);
    };
  }, [items.length, refreshEdge]);

  useEffect(() => {
    const element = scrollerRef.current;
    const active = element?.querySelector<HTMLButtonElement>(
      'button[aria-current="page"]',
    );
    active?.scrollIntoView({ block: "nearest", inline: "nearest" });
    const timer = window.setTimeout(refreshEdge, 0);
    return () => window.clearTimeout(timer);
  }, [selectedId, refreshEdge]);

  const className = [
    "cc-secondary-navigation-wrap",
    edge.scrollable ? "is-scrollable" : "",
    edge.left ? "can-scroll-left" : "",
    edge.right ? "can-scroll-right" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className}>
      <nav
        ref={scrollerRef}
        className="cc-secondary-navigation"
        aria-label="Разделы выбранного экрана"
      >
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={selectedId === item.id ? "active" : ""}
            onClick={() => onSelect(item.id)}
            aria-current={selectedId === item.id ? "page" : undefined}
          >
            <span>{item.label}</span>
            {item.unread && (
              <i className="cc-unread-dot" aria-label="Есть новое" />
            )}
            {item.badge && <small>{item.badge}</small>}
          </button>
        ))}
      </nav>
      <span className="cc-secondary-overflow-cue left" aria-hidden="true" />
      <span className="cc-secondary-overflow-cue right" aria-hidden="true" />
    </div>
  );
}

function setupDigest(setup: CampaignSetup): string {
  return JSON.stringify({
    stage: setup.setupStage,
    phase: setup.worldSelection.phase,
    campaign: [
      setup.campaign.status,
      setup.campaign.memberCount,
      setup.campaign.publicVisible,
    ],
    pendingInvitations: setup.pendingInvitations.map((invitation) => [
      invitation.id,
      invitation.status,
      invitation.createdAt,
    ]),
    members: setup.members.map((member) => [
      member.campaignMemberId,
      member.displayName,
      member.avatar.kind,
      member.avatar.updatedAt,
      member.avatar.telegramUrl,
      member.assignedCharacterId,
      member.assignedOfficeTitle,
    ]),
    proposals: setup.worldSelection.proposals.map((proposal) => [
      proposal.id,
      proposal.revisionNumber,
      proposal.moderationStatus,
      proposal.voteCount,
      proposal.isEligibleForVoting,
    ]),
    vote: setup.worldSelection.myVoteProposalId,
    selected: setup.worldSelection.selectedProposalId,
    generationFailure: [
      setup.worldSelection.generationFailed,
      setup.worldSelection.generationFailureCode,
      setup.worldSelection.generationFailedAt,
    ],
    permissions: [
      setup.worldSelection.canSubmitProposal,
      setup.worldSelection.canOpenVoting,
      setup.worldSelection.canVote,
      setup.worldSelection.canFinalizeVoting,
    ],
    world: setup.world
      ? [
          setup.world.id,
          setup.world.worldName,
          setup.world.openingImages.map((asset) => [
            asset.id,
            asset.status,
            asset.attempts,
            asset.nextAttemptAt,
          ]),
        ]
      : null,
    continuity: [
      setup.continuity.active.map((item) => [
        item.id,
        item.status,
        item.progressPercent,
        item.updatedAt,
      ]),
      setup.continuity.archived.map((item) => [
        item.id,
        item.status,
        item.updatedAt,
      ]),
      setup.continuity.history
        .slice(0, 20)
        .map((event) => [event.id, event.eventType, event.createdAt]),
    ],
    threats: [
      setup.threats.tracks.map((item) => [
        item.id,
        item.currentValue,
        item.level,
        item.criticalCycles,
        item.updatedAt,
      ]),
      setup.threats.recentEvents
        .slice(0, 20)
        .map((event) => [
          event.id,
          event.eventType,
          event.afterValue,
          event.createdAt,
        ]),
      setup.threats.defeat
        ? [setup.threats.defeat.id, setup.threats.defeat.createdAt]
        : null,
    ],
    rolesAssigned: setup.rolesAssigned,
    characters: setup.characters.map((character) => [
      character.id,
      character.campaignMemberId,
      character.publicTitle,
    ]),
    privateProfile: setup.myPrivateProfile
      ? [setup.myPrivateProfile.characterId, setup.myPrivateProfile.officeTitle]
      : null,
    cycle: setup.cycle.current
      ? [
          setup.cycle.current.id,
          setup.cycle.current.phase,
          setup.cycle.current.title,
          setup.cycle.current.dayReadyCount,
          setup.cycle.current.agendaReadyCount,
          setup.cycle.current.actionsUsed,
          setup.cycle.current.myDayReady,
          setup.cycle.current.myAgendaReady,
          setup.cycle.current.mySkippedInitiative,
          setup.cycle.current.actions.map((item) => [
            item.id,
            item.outcome,
            item.visibility,
          ]),
          setup.cycle.current.rooms.map((room) => [
            room.id,
            room.messages.length,
            room.messages.at(-1)?.id,
          ]),
          setup.cycle.current.initiatives.map((initiative) => [
            initiative.id,
            initiative.updatedAt,
            initiative.status,
            initiative.myVote,
            initiative.supportCount,
            initiative.opposeCount,
            initiative.abstainCount,
          ]),
          setup.cycle.current.memberProgress.map((member) => [
            member.userId,
            member.actionsUsed,
            member.dayReady,
            member.hasInitiative,
            member.skippedInitiative,
            member.agendaReady,
          ]),
          setup.cycle.current.openingImages.map((asset) => [
            asset.id,
            asset.status,
            asset.attempts,
          ]),
          setup.cycle.current.closingImages.map((asset) => [
            asset.id,
            asset.status,
            asset.attempts,
          ]),
          setup.cycle.current.worldChanges.map((change) => [
            change.kind,
            change.code,
            change.before,
            change.after,
          ]),
        ]
      : [null, setup.cycle.canStartCycle],
  });
}

function cycleTabBadge(setup: CampaignSetup) {
  const cycle = setup.cycle.current;
  if (!cycle) return setup.cycle.canStartCycle ? "●" : undefined;
  if (cycle.phase !== "PUBLISHED") return "●";
  if (
    cycle.closingImages.some((asset) =>
      ["PENDING", "GENERATING", "RETRY"].includes(asset.status),
    )
  )
    return "●";
  return undefined;
}

function imageTabBadge(assets: MediaAsset[]) {
  return assets.some((asset) =>
    ["PENDING", "GENERATING", "RETRY"].includes(asset.status),
  )
    ? "●"
    : undefined;
}
function stageLabel(stage: CampaignSetup["setupStage"]) {
  return {
    WAITING_FOR_PLAYERS: "Соберите совет",
    COLLECTING_WORLD_PROPOSALS: "Предложения мира",
    WORLD_VOTING: "Голосование",
    WORLD_GENERATING: "Генерация мира",
    RANDOM_ROLE_ASSIGNMENT: "Распределение ролей",
    PRIVATE_BRIEFING: "Личные досье",
    READY: "Эпоха основания",
  }[stage];
}
